/**
 * The CARA search picker, shared by the data browser and the CARA Source node
 * so the two always offer the same search.
 *
 * **CARA has no query language.** Where Veeva has VQL to type, CARA's search
 * endpoint takes a structured body: a *view* (an object type's name doubles as
 * one), an optional free-text term, filters and sorts. So this is a picker, not
 * an editor — and the escape hatch is the raw body itself, for a search the
 * picker cannot express.
 *
 * Filters are emitted as structured entries `{field, operator, value}`; nothing
 * a user types is ever spliced into a query string.
 *
 * The component owns no search state: it reads `value` and reports changes, so
 * the browser can keep them in component state and the node can persist them on
 * its config. The selection's own type lives in `caraSearch.ts`.
 */

import { useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  CARA_FILTER_OPERATORS,
  caraDescribeType,
  caraListTypes,
  type CaraFilterSpec,
  type CaraType,
} from "@/shared/api/integration-cara";
import { toastApiError } from "@/shared/ui/toast";
import type { CaraSearchSelection } from "./caraSearch";

interface Props {
  connectionId: string;
  value: CaraSearchSelection;
  onChange: (patch: Partial<CaraSearchSelection>) => void;
  disabled?: boolean;
}

export function CaraSearchBuilder({ connectionId, value, onChange, disabled = false }: Props) {
  const getToken = useAccessToken();
  const [types, setTypes] = useState<CaraType[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // The tenant's object types. Also the list of things a search can target —
  // in CARA a type name *is* a view id.
  useEffect(() => {
    if (!connectionId) {
      setTypes([]);
      return;
    }
    let cancelled = false;
    setLoadingTypes(true);
    void (async () => {
      try {
        const token = await getToken();
        const items = (await caraListTypes(token, connectionId)).items;
        if (!cancelled) setTypes(items);
      } catch (err) {
        if (!cancelled) toastApiError("Could not load CARA object types", err);
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  // The chosen type's fields, for the column and filter pickers. Dotted paths,
  // because that is what a search result's columns are called.
  useEffect(() => {
    if (!connectionId || !value.typeName) {
      setFields([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const found = (await caraDescribeType(token, connectionId, value.typeName)).fields;
        if (!cancelled) setFields(found.map((f) => f.name));
      } catch (err) {
        if (!cancelled) toastApiError("Could not load fields", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, value.typeName]);

  const setFilter = (index: number, patch: Partial<CaraFilterSpec>) => {
    onChange({
      filters: value.filters.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });
  };

  return (
    <Stack gap="xs">
      <SegmentedControl
        value={value.mode}
        onChange={(mode) => onChange({ mode: mode === "body" ? "body" : "builder" })}
        disabled={disabled}
        size="xs"
        data={[
          { value: "builder", label: "Pick an object" },
          { value: "body", label: "Search body" },
        ]}
      />

      {value.mode === "builder" ? (
        <>
          <Select
            label="Object type"
            description="CARA searches a view; an object type's name is its view."
            placeholder={loadingTypes ? "Loading…" : "Pick an object type"}
            data={types.map((t) => ({ value: t.name, label: `${t.label} (${t.name})` }))}
            value={value.typeName || null}
            disabled={disabled || loadingTypes}
            searchable
            nothingFoundMessage="No object types"
            onChange={(v) => onChange({ typeName: v ?? "", fields: [], filters: [] })}
          />

          {value.typeName && (
            <>
              <MultiSelect
                label="Columns"
                description="Leave empty to keep every column a record carries. Records are sparse, so picking columns is what makes the table the same shape every run."
                data={fields}
                value={value.fields}
                disabled={disabled}
                searchable
                clearable
                onChange={(v) => onChange({ fields: v })}
              />

              <TextInput
                label="Search text (optional)"
                placeholder="A free-text term"
                value={value.text}
                disabled={disabled}
                onChange={(e) => onChange({ text: e.currentTarget.value })}
              />

              <Checkbox
                label="Also search inside document content"
                checked={value.searchInContent}
                disabled={disabled}
                onChange={(e) => onChange({ searchInContent: e.currentTarget.checked })}
              />

              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Filters
                </Text>
                {value.filters.map((filter, index) => (
                  <Group key={index} gap="xs" wrap="nowrap" align="flex-end">
                    <Select
                      placeholder="Field"
                      data={fields}
                      value={filter.field || null}
                      disabled={disabled}
                      searchable
                      onChange={(v) => setFilter(index, { field: v ?? "" })}
                      style={{ flex: 2 }}
                    />
                    <Select
                      data={CARA_FILTER_OPERATORS.map((o) => ({ ...o }))}
                      value={filter.operator}
                      disabled={disabled}
                      allowDeselect={false}
                      onChange={(v) => setFilter(index, { operator: v ?? "eq" })}
                      style={{ flex: 1 }}
                    />
                    <TextInput
                      placeholder="Value"
                      value={filter.value}
                      disabled={disabled}
                      onChange={(e) => setFilter(index, { value: e.currentTarget.value })}
                      style={{ flex: 2 }}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={disabled}
                      aria-label={`Remove filter ${index + 1}`}
                      onClick={() =>
                        onChange({ filters: value.filters.filter((_, i) => i !== index) })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
                <Group>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        filters: [...value.filters, { field: "", operator: "eq", value: "" }],
                      })
                    }
                  >
                    Add filter
                  </Button>
                </Group>
              </Stack>
            </>
          )}
        </>
      ) : (
        <Textarea
          label="CARA search body"
          description='The JSON body CARA&apos;s /api/search accepts, e.g. {"view_id": "cls_document", "version_filter_id": "LATEST"}. A bare view name works too. Use this for a search the picker cannot express.'
          placeholder='{"view_id": "cls_document"}'
          autosize
          minRows={3}
          value={value.body}
          disabled={disabled}
          onChange={(e) => onChange({ body: e.currentTarget.value })}
        />
      )}
    </Stack>
  );
}
